import { setPublicFunnelLanguage } from '@/components/public/publicFunnelLanguage';
// WEB-2054: грелка кусков рендерера блоков по составу воронки.
import { preloadPublicBlockChunksFor } from '@/player/publicBlockChunkLoaders';
import { collectPublicContentBlockTypes } from '@/player/publicContentBlockTypes';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
// WEB-1988: публичный прелоадер без Ant Design (кит-прелоадер тянет
// Typography и theme.useToken(), с ними в бандл покупателя — весь antd).
import { PublicPreloader as Preloader } from '@/components/public/PublicPreloader';
import { collectBlockFontIds } from '@/components/editor/blockFont';
import { FormValuesProvider } from '@/components/editor/FormValuesContext';
import {
  loadQuizProgress,
  type RestoredQuizProgress,
  sweepExpiredQuizProgress,
} from '@/components/quiz-runtime/quizProgressStorage';
// WEB-1906: адреса магазинов проекта блокам страницы — маркер `w2a://store`
// в кнопке-ссылке подменяется настоящим адресом по платформе посетителя.
import { StoreLinksProvider } from '@/components/editor/StoreLinksContext';
import type { EditorScreen } from '@/store/editorSlice';
import { getThemeById } from '@/themes';
import { useStylePackDoor } from '@/themes/useStylePackDoor';
import { usePlayerKitDoor } from '@/player/usePlayerKitDoor';
import { buildFunnelSurfaceStyle } from '@/pages/funnel/funnelSurfaceStyle';
import {
  useFunnelSurfaceOnBody,
  resolveSurfacePageBackground,
} from '@/pages/funnel/useFunnelSurfaceOnBody';
import { ensureFontsLoaded, ensureThemeFontsLoaded } from '@/themes/fonts';
import { useCustomFontFaces } from '@/themes/customFonts';
import { API_BASE_URL } from '@/config/api-url';
import { captureAndLinkProviderProfiles } from '@/utils/providerProfileLink';
import {
  getPublicQuizStrings,
  hasPublicQuizStringsLoaded,
  loadPublicQuizStrings,
} from '@/utils/publicQuizStrings';
import { isOurApexHost } from '@/utils/publicHost';
import { usePreviewLinkMode } from '@/components/public/preview/previewLinkMode';
import { PUBLIC_APEX } from '@/config/public-host';
// WEB-1510: язык посетителя для публичного запроса (порядок приоритетов и его
// обоснование — в самом файле утилиты).
import {
  resolveVisitorLanguages,
  visitorLanguageQuery,
} from '@/utils/visitorLanguage';
import { useTraffback } from '@/utils/analytics/useTraffback';
import { reportConsentDecision } from '@/utils/analytics/trackEvent';
import { readReturnTarget } from '@/utils/analytics/returnTarget';
import type { Logic } from '@/logic-engine';
import {
  loadPixels,
  revokeTrackingConsent,
} from '@/utils/analytics/pixelLoader';
import {
  fireMetaBrowserEvent,
  setPlayerPixelIdentity,
  resolvePlayerConsent,
} from '@/utils/analytics/playerPixelEvents';
import {
  requiresEuConsent,
  readConsentDecision,
  applyConsentDecision,
  type ConsentDecision,
} from '@/utils/analytics/euConsent';
import { ConsentBanner } from '@/components/consent/ConsentBanner';
// WEB-2145 п.3-4: значок «передумать» в углу страницы и адрес политики
// конфиденциальности для баннера (лестница «блок автора → условия → мы»).
import { PublicFooter } from '@/components/public/PublicFooter';
import { resolveConsentPolicyUrl } from '@/utils/analytics/consentPolicyUrl';
// WEB-1330 п.2 (вариант А): авторский блок cookie_consent (палитра
// конструктора) главнее системного баннера — если он стоит на странице, этот
// баннер не поднимается (два баннера согласия — конфликт).
import { hasCookieConsentBlock } from '@/utils/analytics/cookieConsentBlock';
import { isForceConsentActive } from '@/utils/analytics/forceConsent';
import { ConsentActionsProvider } from '@/components/editor/ConsentActionsContext';
// WEB-848: extracted from this file into a reusable module (mirrors the
// paywall player's paywall-runtime, WEB-662 atom 1) so the editor preview
// can render the quiz through the same wizard instead of a separate
// flat-block re-implementation. See quiz-runtime/QuizScreenWizard.tsx.
import {
  QuizScreenWizard,
  hasPaywallStepProof,
  clearPaywallStepProof,
  trackQuizEvent,
} from '@/components/quiz-runtime';
// WEB-2353: подписанный сервером признак возврата с платёжной системы —
// второй ключ к экрану оплаты рядом со следом в браузере.
import {
  currentPresenterId,
  currentReturnPage,
  readCheckoutReturnProof,
  verifyCheckoutReturnProof,
} from '@/components/quiz-runtime/checkoutReturnProof';
import { useAppShownSignal } from '@/public/useAppShownSignal';
import { FunnelPageStyleContext } from '@/pages/funnel/funnelPageStyle';
import { FunnelFaviconContext } from '@/pages/funnel/funnelFavicon';
import { setLegacyPaywallSignalProjectId } from '@/utils/appGuid';
import { sanitizeFaviconUrl, useDocumentFavicon } from '@/utils/favicon';
// WEB-796: относительный путь (не @/) — jest moduleNameMapper резолвит алиас
// раньше css-правила и попытался бы исполнить CSS как JS (скар WEB-782).
import '../styles/public-paywall.css';
import { PublicSystemPageLazy } from '@/components/public/PublicSystemPageLazy';

interface PublicQuizData {
  id: string;
  projectId: string;
  name: string;
  slug: string;
  content: EditorScreen[];
  // Schema version of `content`; future renderers will branch on this when
  // block shapes evolve. Optional for backward-compat with rows written
  // before the column existed.
  contentSchemaVersion?: number;
  settings: Record<string, unknown>;
  language: string;
  // SEAM-0 (P3 pre-F1): additive published-response fields. Optional for
  // backward-compat with responses serialized before the seam landed. The
  // player consumes these in F1 (logic engine) — not here yet.
  logic?: unknown[];
  versionId?: string;
  geo_country?: string | null;
  // P8-F7b slice 2: browser pixel attribution (allowlisted by backend, no token).
  // Optional for backward-compat with quiz rows published before this field landed.
  attribution?: {
    metaPixelId?: string;
    ttPixelId?: string;
  };
  // WEB-256: project-level store URLs for traffback redirect. Optional for backward-compat
  // with responses serialized before the backend added these fields.
  iosStoreUrl?: string | null;
  androidStoreUrl?: string | null;
  // WEB-2140: признак проекта «единое согласие на поддоменах моего домена» —
  // писать куку решения о слежке на весь домен клиента, а не на точный хост.
  // Необязательное: у ответов, собранных до карточки, поля нет, и отсутствие
  // читается как ВЫКЛЮЧЕНО (умолчание, решение владельца `2122-К7`).
  consentShareSubdomains?: boolean;
  // Свежий взгляд пачки 46 (находка 2): «ответы наружу запрещены» — готовый
  // признак сервера для воронки в чувствительной нише (WEB-2159). Считается
  // тем же правилом, что серверный фильтр у точки ухода; ключ ниши наружу не
  // отдаётся. Поля нет у ответа, закешированного до карточки, — отсутствие
  // читается как «не запрещено» (поведение прежнее).
  outboundAnswersBlocked?: boolean;
  // WEB-744 (_funnel-runtime.md §3, ratified PM 2026-07-19): funnel context the
  // BE resolves for this quiz — an active (unpaused) funnel with this quiz and
  // a published paywall (legacy fallback removed, WEB-764). Optional/null
  // for backward-compat with responses serialized before the BE atom landed,
  // and for quiz-only (lead-gen) quizzes that have no funnel at all.
  funnel?: { paywallSlug: string; funnelId: string | null } | null;
  // WEB-2181/WEB-2182: опознавательные признаки воронки для рекламных событий
  // (название квиза и его краткий адрес). Сервер отдаёт блок всегда, неизвестное
  // — null; поле необязательное для совместимости со старыми ответами.
  funnelIdentity?: {
    funnelId?: string | null;
    funnelName?: string | null;
    quizSlug?: string | null;
    quizName?: string | null;
    paywallSlug?: string | null;
    paywallName?: string | null;
  };
  // WEB-2154/WEB-2141: настройки УРОВНЯ ВОРОНКИ — единственная дорога, по
  // которой они доезжают до посетителя (узкий резолвер сервера не требует
  // экрана оплаты, поэтому доезжают и у воронки «только квиз»). Свободный
  // объект: ключи в нём заводят карточки по одному, и читатель разбирает
  // СВОЙ ключ сам — первый из них цель возврата (`returnTarget`).
  // null = воронки нет вовсе; поле необязательное для совместимости со
  // старыми ответами.
  funnelSettings?: {
    funnelId: string;
    settings: Record<string, unknown>;
  } | null;
}

/**
 * WEB-1336 (П2), перенос на квиз (десятка 4, п.1): загрузка различает три
 * мира вместо схлопывания в null. 'not-found' — сервер ОТВЕТИЛ 404 (квиза
 * действительно нет); 'unavailable' — сервер ответил другим отказом (5xx/429)
 * ЛИБО до него не достучались (обрыв сети): временный сбой, показываем
 * «Сервис временно недоступен» с кнопкой «Повторить», а не окончательное
 * «Квиз не найден». Зеркало PublicPaywall.tsx FetchPaywallResult.
 */
type FetchQuizResult =
  | { kind: 'ok'; data: PublicQuizData }
  | { kind: 'not-found' }
  // WEB-1894 п.4: отказ ДОСТУПА к предпросмотру (401/403) — отдельный исход.
  // Раньше он сваливался в `unavailable`, и автору, открывшему ссылку в
  // приватном окне, писали «сервис недоступен» с кнопкой «Повторить»,
  // которая в таком окне не поможет никогда.
  | { kind: 'auth-required' }
  | { kind: 'unavailable' };

// Atom B (WEB slug->projectId): the public renderer resolves by-id — the
// project's slug is no longer part of the public/preview contract.
async function fetchPublicQuiz(
  projectId: string,
  quizSlug: string,
  // WEB-1510: язык посетителя — готовый хвост адреса (`visitorLanguageQuery`:
  // `lang=…` и, при списке языков браузера, `langs=…`, WEB-2471). Пусто = не
  // передаём, сервер отдаёт исходный язык воронки (ровно прежнее поведение).
  langQueryTail: string,
): Promise<FetchQuizResult> {
  try {
    // WEB-1257 (хвост WEB-1403): credentials — чтобы серверная кука
    // qb_visitor_id прикладывалась и здесь (единый ключ посетителя на всех
    // публичных запросах; зеркало publicResolve.ts / trackEvent.ts).
    // WEB-1510: ключ серверного кэша язык учитывает
    // (public-content-cache.util.ts) — версии на разных языках друг друга не
    // подменяют.
    const langQuery = langQueryTail ? `?${langQueryTail}` : '';
    const res = await fetch(
      `${API_BASE_URL}/public/quiz/by-id/${projectId}/${quizSlug}${langQuery}`,
      {
        credentials: 'include',
      },
    );
    if (res.status === 404) return { kind: 'not-found' };
    if (!res.ok) return { kind: 'unavailable' };
    const data = await res.json();
    return { kind: 'ok', data: data as PublicQuizData };
  } catch {
    return { kind: 'unavailable' };
  }
}

// WEB-411: authenticated draft preview (editor "Preview" button). Raw fetch with
// the JWT (read directly from localStorage to avoid importing the axios client,
// whose module-level import.meta breaks jest). The BE returns the DRAFT to
// project members only, bypassing the public/FREE-tier gate. !ok → not-found → 404.
async function fetchPreviewQuiz(
  projectId: string,
  quizSlug: string,
): Promise<FetchQuizResult> {
  try {
    const token = localStorage.getItem('access_token');
    const res = await fetch(
      `${API_BASE_URL}/quizzes/preview/by-id/${projectId}/${quizSlug}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, // i18n-allow: схема заголовка авторизации, не текст человеку
      },
    );
    if (res.status === 404) return { kind: 'not-found' };
    // WEB-1894 п.4: ручка закрыта JwtAuthGuard — без токена 401, с чужой
    // сессией 403. Ни то, ни другое не сбой сервиса.
    if (res.status === 401 || res.status === 403) {
      return { kind: 'auth-required' };
    }
    if (!res.ok) return { kind: 'unavailable' };
    return { kind: 'ok', data: (await res.json()) as PublicQuizData };
  } catch {
    return { kind: 'unavailable' };
  }
}

/**
 * WEB-1482 — какой пейволл показать после квиза.
 *
 * Порядок (сверху вниз, первый непустой выигрывает):
 *  1. `previewPaywallSlug` — превью редактора, инструмент автора; обязано
 *     оставаться главнее всего, иначе автор не посмотрит свой черновик.
 *  2. `paywallOverrideSlug` — пейволл, ВЫБРАННЫЙ направлением A/B-теста или
 *     маршрутом воронки. Сервер отдаёт его, только если пейволл того же
 *     проекта и в состоянии ACTIVE (`/public/resolve`, fail-closed).
 *  3. `quiz.funnel` — пейволл, подобранный сервером по эвристике «самая новая
 *     активная воронка на этот квиз» (funnel-link.resolver.ts). Остаётся
 *     запасным путём: именно он показывался ВСЕГДА до этой правки.
 *
 * funnelId у переопределения намеренно null: выбранный пейволл может не
 * принадлежать той воронке, которую резолвер подобрал для квиза, и приписать
 * ему чужой идентификатор воронки значило бы тихо испортить будущую
 * атрибуцию. Пустое значение заметно, неверное — нет. (Так же ведёт себя
 * ветка превью, существовавшая до WEB-1482.)
 */
function resolveFunnelContext(
  quizFunnel: PublicQuizData['funnel'],
  previewPaywallSlug: string | null,
  paywallOverrideSlug: string | undefined,
): { paywallSlug: string; funnelId: string | null } | null {
  if (previewPaywallSlug) {
    return { paywallSlug: previewPaywallSlug, funnelId: null };
  }
  if (paywallOverrideSlug) {
    return { paywallSlug: paywallOverrideSlug, funnelId: null };
  }
  return quizFunnel ?? null;
}

interface PublicQuizPageProps {
  /**
   * When the page is rendered through `PublicHostRouter` for a user-owned
   * domain, the identifiers cannot be derived from URL params. The host
   * router resolves them via `/public/resolve` and forwards them here,
   * overriding both the URL-param path and the subdomain inference.
   *
   * Atom B (WEB slug->projectId): carries the project's id, not its slug —
   * the public/preview fetches below resolve by-id.
   *
   * WEB-1482: `paywallOverrideSlug` — слаг пейволла, выбранного A/B-тестом
   * или маршрутом воронки (см. resolveFunnelContext выше). Необязателен:
   * обычный доменный маршрут на квиз его не несёт.
   */
  routeOverride?: {
    projectId: string;
    quizSlug: string;
    paywallOverrideSlug?: string;
    /**
     * WEB-1989 шаг 1 — ГОТОВОЕ тело публичной выдачи, приехавшее вместе с
     * `/public/resolve` (то же самое, что вернул бы `by-id`, поле в поле —
     * сторож равенства на бэке: public-resolve-content-parity.web1989.spec.ts).
     * Есть тело — второго запроса к API нет вовсе. Нет ключа — прежний путь.
     */
    prefetched?: Record<string, unknown>;
    /**
     * WEB-1989 шаг 2 — ответ пришёл из последней хорошей копии воркера
     * (бэк недоступен). Квизу это ничего не меняет: он проходится по уже
     * полученному телу. Ключ объявлен, чтобы роутер мог передавать одну и ту
     * же пару полей обеим страницам, и чтобы признак был виден в отладке.
     */
    servedStale?: boolean;
    /**
     * WEB-3913 — страница съёмки службой разбора (`pages/PublicShoot.tsx`):
     * тело передано ею самой, порог апекса не действует. Ставит только она.
     */
    shoot?: boolean;
  };
}

export default function PublicQuizPage({
  routeOverride,
}: PublicQuizPageProps = {}) {
  // projectId can arrive two ways:
  //   - routeOverride from PublicHostRouter (public apex/subdomain or a
  //     user-owned domain — resolved server-side via /public/resolve) — wins
  //   - URL path param (`/<projectId>/<quizSlug>`) — editor preview / dev mode
  // Atom B (WEB slug->projectId): the project's slug is no longer part of
  // this contract — both sources now carry the project's id, matching the
  // editor Preview button (QuizEditor.tsx) which opens this route with the
  // project's id in that slot.
  const params = useParams<{ projectId?: string; quizSlug?: string }>();
  const projectId = routeOverride?.projectId ?? params.projectId;
  const quizSlug = routeOverride?.quizSlug ?? params.quizSlug;

  // WEB-1573: назвать проект счётчику легаси-обращений ДО любого резолва
  // guid (резолв зовут эффекты ниже, тело компонента отрабатывает раньше).
  // Пинг старого формата ссылки уходит из синхронной утилиты appGuid, которая
  // про проект не знает, — а без имени проекта требование решения владельца
  // 23.08 «назвать поимённо, кто ещё ходит по старому пути» по каналу оплаты
  // выполнить нечем (замер 26.08: ~21 обращение в сутки, ни одного имени).
  setLegacyPaywallSignalProjectId(projectId);

  // WEB-1482: пейволл, выбранный A/B-тестом/маршрутом воронки. Приходит
  // только через routeOverride (его источник — /public/resolve), из адресной
  // строки НЕ читается: иначе любой посетитель подставил бы себе чужой
  // экран оплаты параметром в ссылке.
  const paywallOverrideSlug = routeOverride?.paywallOverrideSlug;
  // WEB-1989: тело показа, приехавшее одним запросом вместе с резолвом.
  const prefetched = routeOverride?.prefetched;
  const [searchParams] = useSearchParams();
  // WEB-411: editor "Preview" opens `?preview=1` — an authed draft preview for
  // project members. Preview is exempt from the apex serving block.
  // WEB-2115: или страница открыта по ссылке предпросмотра (посторонний
  // человек, поддомен `preview.<апекс>`): тело — у загрузчика ссылки, режим
  // «не боевой» тот же. Признак приходит контекстом от страницы
  // предпросмотра, из адреса его не включить.
  const previewLink = usePreviewLinkMode();
  const isPreview = searchParams.get('preview') === '1' || !!previewLink;
  // WEB-1820: служебный параметр — принудительный показ баннера согласия ВНЕ
  // ЕС, единственный способ проверить его живьём браузером, выходящим в сеть
  // не из ЕС/Великобритании. Работает ТОЛЬКО на хостах стенда платформы
  // (isForceConsentActive → isStagingConsentHost); на купленном клиентском
  // домене и в бою игнорируется по устройству хоста, ничего не пишет.
  const forceConsentActive = isForceConsentActive(
    searchParams.get('forceConsent'),
    typeof window !== 'undefined' ? window.location.hostname : '',
    PUBLIC_APEX,
  );
  // Предзагрузка iOS SDK 0.8.0, защита «от дурака». SDK грузит заранее в
  // фоновом WebView (`?preload=1`) адрес из поля кабинета «адрес
  // веб-пейволла», а туда можно вписать и воронку, начинающуюся с квиза.
  // Одна дверь: пока SDK не сообщил о показе, квиз не загружается вовсе
  // (эффект загрузки ниже ждёт appShown, страница держит обычную загрузку) —
  // в фоне не уходят ни пиксели, ни QUIZ_VIEW, ни просмотры экранов и замер
  // скорости визарда. Без `preload=1` сигнал считается пришедшим сразу.
  const appShown = useAppShownSignal(searchParams.get('preload') === '1');
  // WEB-795: the funnel row's "Превью" action opens the quiz preview with
  // `?previewPaywallSlug=<slug>` when the funnel this quiz belongs to also
  // has a paywall. The authenticated preview endpoint
  // (getPreviewQuizByProjectId) always returns `funnel: null` (WEB-744:
  // funnel-context resolution requires an ACTIVE/published funnel+paywall —
  // out of scope for a draft preview), so without this client-side override
  // a funnel preview would always dead-end at the generic "Thank you" stub
  // instead of walking into the paywall's own draft content. Gated on
  // isPreview so it can never affect real public traffic; the paywall
  // preview fetch triggered downstream re-checks project membership on its
  // own (same projectId), so this cannot leak another project's draft.
  // WEB-2115: в режиме ссылки слаг из адреса ни на что не влияет — пейволл
  // берётся у загрузчика ссылки (PublicPaywall смотрит его первым).
  const previewPaywallSlug = isPreview
    ? searchParams.get('previewPaywallSlug')
    : null;
  // WEB-744 этап 1.5: `?s=paywall` — маркер «посетитель на шаге пейволла
  // воронки». Ставится replaceState'ом при инлайн-переходе квиз→пейволл и
  // входит в success/cancel URL Stripe-чекаута, чтобы возврат с оплаты и
  // перезагрузка страницы вели на пейволл, а не на квиз с начала. Без
  // funnel-контекста в данных квиза маркер игнорируется.
  //
  // WEB-1740: одной метки МАЛО. Она лежит в адресной строке у покупателя на
  // виду — скопировал адрес, переслал, и получатель попадал прямо на оплату:
  // квиз ему не показывали, ответов он не оставлял, лида не создавал, события
  // квиза не стреляли (ниже они намеренно заглушены на шаге пейволла). У
  // владельца воронки покупки висели без прохождений под ними. Поэтому метку
  // подтверждаем следом, который оставил ИМЕННО ЭТОТ браузер в момент
  // настоящего перехода на пейволл (paywallStepProof).
  //
  // След читаем ОДИН раз на загрузку: это свойство того, КАК страница
  // открылась, а не живое состояние. Иначе появление следа посреди сессии
  // (посетитель дошёл до пейволла в этой же вкладке) перевело бы
  // wantsPaywallStep из false в true и перезапустило бы загрузку квиза ниже.
  const [hasPaywallStepProofAtLoad] = useState(() =>
    hasPaywallStepProof(projectId, quizSlug),
  );
  /**
   * WEB-2353 (усиление WEB-2349, решение владельца 22.09) — ВТОРОЙ ИСТОЧНИК
   * ПРАВА НА ЭКРАН ОПЛАТЫ: ПОДПИСАННЫЙ НАМИ ПРИЗНАК В АДРЕСЕ ВОЗВРАТА.
   *
   * След живёт в браузере, и этого мало: смена браузера/устройства между
   * уходом на оплату и возвратом, чистка хранилища, «инкогнито» и отказ
   * посетителя от хранения (WEB-2349) роняли УЖЕ ЗАПЛАТИВШЕГО человека на
   * первый экран квиза. Поэтому сервер при создании платёжной сессии кладёт в
   * адреса возврата короткоживущий подписанный признак и сам же его проверяет
   * (`checkoutReturnProof.ts`, ручка `/public/paywall/return-proof/:projectId`).
   *
   * Признак читается ОДИН раз на загрузку — по той же причине, что и след:
   * это свойство того, КАК страница открылась.
   *
   * Спрашиваем сервер только когда признак реально может что-то изменить:
   * есть метка шага оплаты, есть признак, следа НЕТ. Со следом ответ уже
   * известен, и лишний запрос на возврате с оплаты не нужен.
   */
  const [returnProofTokenAtLoad] = useState(() =>
    isPreview ? null : readCheckoutReturnProof(searchParams),
  );
  const [returnProofState, setReturnProofState] = useState<
    'none' | 'checking' | 'valid' | 'invalid'
  >(() =>
    !isPreview &&
    searchParams.get('s') === 'paywall' &&
    !hasPaywallStepProofAtLoad &&
    returnProofTokenAtLoad &&
    projectId
      ? 'checking'
      : 'none',
  );
  useEffect(() => {
    if (returnProofState !== 'checking') return;
    if (!projectId || !returnProofTokenAtLoad) {
      setReturnProofState('invalid');
      return;
    }
    let cancelled = false;
    // Фикс волны 3 свежего взгляда пачки 46 (находка 2): вместе с признаком
    // называем СТРАНИЦУ, на которой его предъявляем. Сервер чеканил признак
    // под конкретный адрес возврата и сверяет их — иначе пересланный адрес
    // сутки открывал шаг оплаты у любого квиза проекта (возврат WEB-1740).
    // Вторая половина фикса на сервере: признак годен ровно один раз.
    void verifyCheckoutReturnProof(
      projectId,
      returnProofTokenAtLoad,
      currentReturnPage(),
      // Фикс волны 6 (находка 1): называем себя случайным номером этой
      // загрузки. Иначе короткое окно повтора у сервера (заведено для
      // потерянного ответа) открывало бы шаг оплаты и тому, кому адрес
      // переслали: страница у него та же самая по построению.
      currentPresenterId(),
    ).then((valid) => {
      if (!cancelled) setReturnProofState(valid ? 'valid' : 'invalid');
    });
    return () => {
      cancelled = true;
    };
  }, [returnProofState, projectId, returnProofTokenAtLoad]);
  /**
   * Прогресс, восстановленный на ЭТУ загрузку (заполняется ниже, когда квиз
   * уже загружен). `undefined` — ещё не читали, `null` — читать нечего или
   * нечего восстанавливать. Чтение — действие (см. комментарий у заполнения),
   * поэтому оно обязано случиться ровно один раз.
   */
  const restoredProgressRef = useRef<RestoredQuizProgress | null | undefined>(
    undefined,
  );
  // WEB-2353: право на экран оплаты даёт ЛИБО след этого браузера (WEB-1740),
  // ЛИБО признак из адреса, который подтвердил наш сервер. Подделка,
  // просрочка и сбой проверки дают 'invalid' — то есть ровно прежнее
  // поведение: защита WEB-1740 не ослаблена, у неё появился второй ключ.
  const wantsPaywallStep =
    !isPreview &&
    searchParams.get('s') === 'paywall' &&
    (hasPaywallStepProofAtLoad || returnProofState === 'valid');
  // WEB-1510: язык публичной выдачи. `?lang` → запомненный на визит выбор →
  // язык браузера → null (исходный язык воронки). Превью редактора язык не
  // запрашивает: там языком владеет переключатель редактора, а черновичная
  // ручка перевода не отдаёт.
  // WEB-2471 п.2: в запрос уходит весь список языков браузера
  // (`lang=<первый>&langs=<все>`), сервер берёт первый с переводом. Строка, а
  // не массив, — она же зависимость эффекта загрузки ниже.
  const visitorLang = isPreview
    ? ''
    : visitorLanguageQuery(resolveVisitorLanguages(searchParams.get('lang')));
  const [quiz, setQuiz] = useState<PublicQuizData | null>(null);
  /**
   * WEB-2177 (второй пункт решения владельца 17.09.2026) — УБОРКА ДЛЯ ТЕХ, КТО
   * НЕ ВЕРНУЛСЯ.
   *
   * Прогресс прохождения живёт в браузере покупателя сутки, но снималось
   * просроченное только внутри чтения/записи прогресса СВОЕЙ воронки, то есть
   * лишь когда человек вернулся и у страницы есть все три анкера (проект,
   * квиз, версия содержимого). В самом частом случае — бросил квиз и не
   * вернулся — уборка не срабатывала никогда, и запись лежала бессрочно.
   *
   * Поэтому уборка зовётся на ЛЮБОЙ загрузке публичной страницы воронки и не
   * зависит ни от того, та ли это воронка, ни от того, есть ли у неё версия
   * содержимого. Другого фона у браузера для нас нет (см. докблок
   * `sweepExpiredQuizProgress`): наш код выполняется только когда человек
   * открыл нашу страницу.
   *
   * Предпросмотр автора исключён по тому же правилу, что чтение и запись
   * прогресса: канва редактора к хранилищу покупателя не прикасается.
   */
  useEffect(() => {
    if (isPreview) return;
    sweepExpiredQuizProgress();
  }, [isPreview]);
  // WEB-2022: язык загруженной воронки — системным текстам публичной страницы
  // (аварийный экран стоит выше маршрутизатора и сам его не знает).
  useEffect(() => {
    setPublicFunnelLanguage(quiz?.language ?? null);
  }, [quiz?.language]);
  /**
   * WEB-2057: надписи воронки (счётчик шагов, кнопки, лоадер) приезжают
   * пакетом по языку воронки. Пока пакет не на руках, содержимое не
   * показывается — иначе покупатель успел бы увидеть английские надписи и
   * перескок на свой язык следующим кадром. Отказ пакета (файл унесло
   * выкатом) не держит страницу на прелоадере вечно: рисуем с английскими
   * надписями — это прежнее поведение незнакомого языка.
   */
  // WEB-2534: стиль-пак воронки приезжает лениво — страница ждёт его вместе с
  // надписями, до первого кадра (см. докблок useStylePackDoor).
  const stylePackDoor = useStylePackDoor([
    quiz?.settings?.stylePackId as string | undefined,
  ]);
  // WEB-3447 (заход 3 — одна дверь вместо трёх, решение владельца 17.10):
  // вычисляемые значения ({{bmi}} и др.), рамка экрана Slimkit и градиенты с
  // переменными пака едут одним ленивым куском и только воронке, которой
  // нужен хоть один набор (`usePlayerKitDoor`). Страница ждёт кусок вместе с
  // паком, до первого кадра: пустого {{bmi}}, экрана без рамки эталона и
  // сплошного цвета вместо градиента покупатель не увидит; визард берёт
  // набор рамки один раз при монтировании.
  const quizStylePackId = quiz?.settings?.stylePackId as string | undefined;
  const playerKitDoor = usePlayerKitDoor(
    quiz,
    quiz?.settings?.themeId,
    [quizStylePackId],
    quizStylePackId,
    // Слово владельца 20.10.2026: шеврон и крестик шапки своими полями тоже
    // рисует кусок рамки — заказываем его и по ним.
    quiz?.settings?.quizHeader,
    // Слово владельца 21.10.2026: прибитую кнопку и затемнение своим полем
    // кнопки тоже рисует кусок рамки.
    quiz?.settings?.quizCta,
    // Прибитую кнопку отдельного экрана (поле экрана ctaPlacement) — тоже.
    quiz?.content,
  );
  const [stringsState, setStringsState] = useState<'wait' | 'ready' | 'failed'>(
    'ready',
  );
  /**
   * ДВЕРЬ ЗАКРЫВАЕТСЯ ТЕМ ЖЕ КАДРОМ, ЧТО ПОКАЗЫВАЕТСЯ ТЕЛО (ревизия пачки 36,
   * Ф1). Заказ пакета жил только в эффекте ниже, а эффект исполняется ПОСЛЕ
   * кадра: ответ резолва ставил тело и снимал прелоадер одним обработчиком,
   * поэтому первый кадр рисовал содержимое с английскими надписями
   * (`getPublicQuizStrings` синхронна, пакета ещё нет), эффект возвращал
   * прелоадер, и только третьим кадром приезжал нужный язык. На медленной сети
   * покупатель видел мигание «английское → крутилка → своё».
   *
   * Теперь язык, узнанный из ответа, закрывает дверь СИНХРОННО — в том же
   * обработчике, до `setIsLoading(false)`: React сводит оба состояния в один
   * кадр, и содержимого по-английски не бывает ни разу. Языки ядра (en/ru) и
   * уже приехавший пакет ничего не ждут — дверь остаётся открытой.
   */
  const closeStringsDoorFor = (language?: string | null): void => {
    if (!language || hasPublicQuizStringsLoaded(language)) return;
    setStringsState('wait');
    // Отказ обработает эффект ниже (тот же заказ, второго запроса не будет);
    // здесь глушим, чтобы не оставить необработанный промис.
    void loadPublicQuizStrings(language).catch(() => undefined);
  };
  useEffect(() => {
    const language = quiz?.language;
    if (!language || hasPublicQuizStringsLoaded(language)) {
      setStringsState('ready');
      return;
    }
    let cancelled = false;
    setStringsState('wait');
    loadPublicQuizStrings(language)
      .then(() => {
        if (!cancelled) setStringsState('ready');
      })
      .catch(() => {
        if (!cancelled) setStringsState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [quiz?.language]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // WEB-1336 (П2), перенос на квиз (десятка 4, п.1): обрыв сети/5xx — отдельное
  // состояние, НЕ «не найдено»; retryNonce перезапускает загрузку кнопкой
  // «Повторить» (зеркало PublicPaywall.tsx).
  const [unavailable, setUnavailable] = useState(false);
  // WEB-1894 п.4: «нужен вход» — отдельное состояние от «сервис недоступен».
  const [authRequired, setAuthRequired] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  // WEB-3826 доводка, находка 8: «Повторить» нажато, ответа ещё нет — страница
  // «сервис недоступен» остаётся, кнопка показывает загрузку. Снимает ответ.
  const [retrying, setRetrying] = useState(false);
  // One-shot guard: each event key fires at most once per mount.
  // Survives React StrictMode double-invoke of effects.
  const firedOnce = useRef<Set<string>>(new Set());

  // WEB-409 (P8-F9): EU consent overlay visibility — decided after the quiz
  // loads and geo_country is known. Pixels load only once consent is granted.
  const [showConsent, setShowConsent] = useState(false);

  // WEB-2145 п.3: значок «передумать» показывается ровно тому, у кого согласие
  // вообще спрашивали (посетитель из ЕС/неизвестной страны) и кто уже ответил.
  // Обе величины держим состоянием, а не читаем куку при отрисовке:
  // `readConsentDecision()` — обращение к `document.cookie`, оно не заставляет
  // React перерисоваться, и значок не появился бы сразу после нажатия кнопки.
  const [euVisitor, setEuVisitor] = useState(false);
  const [consentDecision, setConsentDecision] = useState<
    ConsentDecision | undefined
  >(undefined);

  // WEB-921 (находка PM 30.07, десктоп): на пути воронки пейволл рендерится
  // ВНУТРИ этих обёрток (WEB-744 §3 / WEB-871), поэтому его тема доставалась
  // только внутреннему узлу шириной 680, а весь вьюпорт продолжала красить
  // тема КВИЗА — светлый пейволл выглядел узкой полосой на тёмной странице.
  // Страницей владеет этот компонент, поэтому встроенный экран лишь ОБЪЯВЛЯЕТ
  // свой страничный стиль (см. funnelPageStyle.ts), а применяем его здесь.
  // Хук объявлен вместе с остальными — ДО любых ранних return (шрам
  // «Rendered more hooks», WEB-890).
  const [funnelPageStyle, setFunnelPageStyle] =
    useState<React.CSSProperties | null>(null);

  // WEB-928: иконкой вкладки на пути воронки владеет эта страница (тот же
  // приём, что с фоном выше): встроенный пейволл лишь ОБЪЯВЛЯЕТ свою, а
  // решаем здесь. Флаг квиза settings.faviconApplyToFunnel = «моя иконка на
  // всю воронку» и перебивает объявление пейволла; без флага пейволл со своей
  // иконкой побеждает, а без своей — остаётся иконка квиза. Флаг читается
  // МЯГКО (решение PM 2026-07-31): при пустом поле у квиза подхватываем
  // иконку пейволла — иначе взведённый флаг гасил бы единственную заданную
  // иконку воронки. Хуки объявлены
  // вместе с остальными, ДО ранних return (шрам «Rendered more hooks»,
  // WEB-890).
  const [embeddedFavicon, setEmbeddedFavicon] = useState<string | null>(null);
  const quizFavicon = sanitizeFaviconUrl(quiz?.settings?.faviconUrl);
  const faviconLockedToQuiz = quiz?.settings?.faviconApplyToFunnel === true;
  useDocumentFavicon(
    faviconLockedToQuiz
      ? (quizFavicon ?? embeddedFavicon)
      : (embeddedFavicon ?? quizFavicon),
  );

  // WEB-989: фон поверхности — ещё и на html/body (телефон показывает область
  // за пределами main при езде адресной строки/оверскролле, и она была белой).
  // Берём фон ПОСЛЕ слоя встроенного пейволла — как его видит <main> ниже.
  // Хук объявлен вместе с остальными, ДО ранних return (шрам WEB-890).
  useFunnelSurfaceOnBody(
    quiz
      ? resolveSurfacePageBackground({
          ...buildFunnelSurfaceStyle(quiz.settings),
          ...(funnelPageStyle ?? {}),
        })
      : undefined,
  );

  // WEB-395 (SCR 2026-06-30): funnels render ONLY on user-owned custom domains.
  // On our apex host and its subdomains nothing is served — the renderer 404s
  // (a domain is the paid serving gate; no domain → no public delivery).
  // Preview is exempt from the apex block (WEB-411): a creator must be able to
  // preview on our domain before assigning a domain / publishing.
  // WEB-2115: в `isPreview` входит и второе исключение — ссылка
  // предпросмотра (её поддомен — поддомен нашего апекса); включается только
  // контекстом страницы предпросмотра. Порог для всего остального не тронут.
  // WEB-3913: и съёмка службой разбора (страница `/__shoot` на хосте рендера):
  // признак ставит только страница съёмки, и тело у неё — только переданное
  // ей самой (player/shootMode.ts), ни за какой воронкой она не ходит.
  const servingBlocked =
    isOurApexHost() && !isPreview && !routeOverride?.shoot;

  useEffect(() => {
    // Предзагрузка SDK: до сигнала показа не делаем ничего (см. appShown выше).
    if (!appShown) return;
    if (servingBlocked) return;
    // WEB-2353: пока сервер не сказал, наш ли признак возврата, загрузку не
    // начинаем вовсе. Иначе первый проход прошёл бы с wantsPaywallStep=false
    // — покупателю на секунду показали бы первый экран квиза и на возврате с
    // оплаты стрельнул бы просмотр квиза (на шаге оплаты он намеренно
    // заглушён ниже). Страница в это время держит свой обычный прелоадер.
    if (returnProofState === 'checking') return;
    // WEB-386 (Z4-6): an unresolvable slug (e.g. the single-segment catch-all
    // matching an unrelated top-level path like `/links`, with no subdomain to
    // supply projectId) previously bailed out here without ever flipping
    // isLoading — the page hung on the spinner forever instead of 404ing.
    if (!projectId || !quizSlug) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }
    // Десятка 4, п.5 (зеркало PublicPaywall.tsx): защита от гонки — с
    // retryNonce повторный запуск эффекта мог получить ответ ПОСЛЕ более
    // свежего запроса и перекрыть его состояние протухшим results. cancelled
    // выставляется при следующем запуске/размонтировании эффекта.
    let cancelled = false;
    // WEB-1139 (П1): WebView мог дописать ?adapty_profile_id=/?revenuecat_profile_id=
    // — связать с guid (fire-and-forget, превью не шлёт реального трафика).
    if (!isPreview) captureAndLinkProviderProfiles(projectId);
    // WEB-1989 шаг 1: тело уже на руках (приехало вместе с резолвом домена) —
    // второго запроса к API не делаем. Превью редактора этой веткой НЕ
    // пользуется: там своя черновичная ручка и свой язык.
    const load: Promise<FetchQuizResult> = previewLink
      ? (previewLink('quiz') as Promise<FetchQuizResult>)
      : isPreview
        ? fetchPreviewQuiz(projectId, quizSlug)
        : prefetched
          ? Promise.resolve({
              kind: 'ok',
              data: prefetched as unknown as PublicQuizData,
            })
          : fetchPublicQuiz(projectId, quizSlug, visitorLang);
    load.then((result) => {
      if (cancelled) return;
      // WEB-3826 доводка: ответ пришёл — повтор окончен. Отдельной записью:
      // повторный отказ `setUnavailable(true)` при `true` страницу не
      // перерисовал бы. Страница «недоступен» держится до ответа, поэтому
      // признак ставится и снимается здесь, а не в «Повторить».
      setRetrying(false);
      setUnavailable(result.kind === 'unavailable');
      if (result.kind === 'auth-required') {
        // WEB-1894 п.4: предпросмотр без сессии — причина в входе, не в
        // сервисе; «Повторить» здесь бесполезно и потому не показывается.
        setAuthRequired(true);
        setIsLoading(false);
      } else if (result.kind === 'unavailable') {
        // WEB-1336 (П2): временный сбой — показать «Повторить», не хоронить
        // купленный трафик окончательным «не найдено».
      } else if (result.kind === 'not-found') {
        setNotFound(true);
      } else {
        const data = result.data;
        // WEB-2054: куски рендерера блоков — по составу воронки, сразу как тело
        // на руках. Вторая дверь к той же грелке: на слаг-адресах (не на
        // клиентском домене) резолва хоста нет вовсе, и без этой строки состав
        // узнавался бы только в момент отрисовки блока.
        preloadPublicBlockChunksFor(collectPublicContentBlockTypes(data));
        // Ф1: язык известен ЗДЕСЬ — дверь надписей закрывается до того, как
        // тело воронки попадёт в кадр (см. док-блок closeStringsDoorFor).
        closeStringsDoorFor(data.language);
        setQuiz(data);
        document.title = data.name;
        // Preview is not real traffic — never fire analytics/pixels for it.
        if (isPreview) {
          setIsLoading(false);
          return;
        }
        // WEB-744 этап 1.5: fast-forward на шаг пейволла — квиз в этой
        // загрузке посетителю не показывается, поэтому квизовые pixels/
        // consent/QUIZ_VIEW не стреляют (иначе перезагрузка пейволла
        // инфлейтит QUIZ_VIEW). Всем этим владеет PublicPaywallPage.
        // WEB-1482: перематывать надо на ДЕЙСТВУЮЩИЙ пейволл, а не только на
        // пейволл воронки. Иначе для квиза без своей воронки, но с
        // переопределением, метка не срабатывала и возврат с оплаты выкидывал
        // посетителя на первый экран квиза.
        if (
          wantsPaywallStep &&
          resolveFunnelContext(
            data.funnel,
            previewPaywallSlug,
            paywallOverrideSlug,
          )?.paywallSlug
        ) {
          setIsLoading(false);
          return;
        }
        // WEB-409 (P8-F9): consent gate. Known non-EU → auto-grant (no banner).
        // EU/unknown → show the prompt unless a decision already exists; pixels
        // stay off until granted (fail-closed). Server stays the EU arbiter
        // (geo_country echoed from cf-ipcountry).
        //
        // WEB-1820: под forceConsentActive авто-grant пропускаем целиком —
        // иначе служебный параметр опоздал бы на один такт (кука 'granted'
        // уже стоит к моменту следующей проверки) и баннер решил бы, что
        // решение уже есть.
        //
        // WEB-2145 (решение оркестратора пачки 45): авто-согласие идёт тем же
        // путём, что и нажатие «Принять» — `applyConsentDecision`. Прежний
        // `writeConsentDecision` только писал куку и не дозаписывал метки из
        // адресной строки, то есть у не-ЕС посетителя путь расходился с
        // ЕС-посетителем без всякой на то причины.
        if (
          !forceConsentActive &&
          !requiresEuConsent(data.geo_country) &&
          !readConsentDecision()
        ) {
          // WEB-2140: признак проекта едет тем же решением — иначе
          // авто-согласие переписало бы доменную куку, поставленную на
          // соседнем поддомене, хостовой, и решение человека «пропало» бы.
          applyConsentDecision('granted', {
            shareAcrossSubdomains: Boolean(data.consentShareSubdomains),
          });
        }
        // WEB-2145 п.3: кому предлагали решить и что он уже решил — в
        // состояние, чтобы значок «передумать» знал, показываться ли ему.
        setEuVisitor(forceConsentActive || requiresEuConsent(data.geo_country));
        setConsentDecision(readConsentDecision());
        // WEB-1330 п.2 (вариант А): автор уже поставил свой блок
        // cookie_consent на странице — он ведёт согласие сам (тем же
        // w2a_consent), системный баннер поверх него не поднимаем.
        //
        // WEB-1820: forceConsentActive поднимает баннер БЕЗ оглядки на гео и
        // на уже записанное решение — на общем QA-домене могла остаться
        // чужая кука 'granted' от прошлой волны тестирования, и без этого
        // сброса параметр был бы бессилен ровно там, где нужен.
        if (
          (forceConsentActive ||
            (requiresEuConsent(data.geo_country) &&
              readConsentDecision() === undefined)) &&
          !hasCookieConsentBlock(data.content)
        ) {
          setShowConsent(true);
        }
        // WEB-2182: признаки события (название квиза, краткий адрес) кладём
        // ДО загрузки пикселя — просмотр страницы уходит сразу за ней, и без
        // этого первое же событие ушло бы обезличенным. Данные только
        // серверные (`funnelIdentity`), из адреса ничего не собираем.
        setPlayerPixelIdentity(data.funnelIdentity);
        // P8-F7b slice 2: init browser pixel once attribution is available.
        // Consent resolved fail-closed from the w2a_consent cookie (WEB-409).
        loadPixels(data.attribution, resolvePlayerConsent());
        if (!firedOnce.current.has('QUIZ_VIEW')) {
          firedOnce.current.add('QUIZ_VIEW');
          trackQuizEvent(projectId, quizSlug, 'QUIZ_VIEW');
          // Browser pixel: PageView (one-shot; no-op if fbq not loaded / no consent).
          fireMetaBrowserEvent('QUIZ_VIEW');
        }
      }
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    quizSlug,
    servingBlocked,
    isPreview,
    // WEB-2115: загрузчик ссылки предпросмотра — стабилен на всю версию
    // (страница предпросмотра пересоздаёт его только сменой версии).
    previewLink,
    wantsPaywallStep,
    retryNonce,
    // WEB-1510: смена языка = другое содержимое, загрузку надо повторить.
    visitorLang,
    // WEB-1482: эффект теперь читает оба слага при разборе метки ?s=paywall.
    previewPaywallSlug,
    paywallOverrideSlug,
    // WEB-1820: forceConsent решает, поднимать ли баннер и обходить ли
    // авто-grant — эффект обязан перезапуститься, если параметр появился/
    // исчез между загрузками (тот же принцип, что у visitorLang выше).
    forceConsentActive,
    // WEB-2353: пока признак возврата проверяется, загрузка не идёт — как
    // только ответ пришёл, эффект обязан запуститься.
    returnProofState,
    // Предзагрузка SDK: сигнал показа пришёл — загрузка обязана запуститься.
    appShown,
  ]);

  // WEB-447 A3: load the Google Fonts stylesheet for the AI-selected font
  // once the quiz data is known. ensureFontsLoaded is idempotent (dedupes by
  // href), so re-running this on every quiz change is safe.
  // WEB-447 wave 1 slice 1: also load the active theme preset's own fonts —
  // previously the 25 presets' fontFamily/headingFontFamily were never
  // requested from Google Fonts at all and silently fell back to the system
  // font. This runs in addition to (not instead of) the fontId override
  // above; ensureThemeFontsLoaded is a no-op when the theme has no theme id
  // or only generic/system families.
  // Свой шрифт воронки (слово владельца 20.10.2026): правила @font-face
  // загруженных автором файлов из settings.customFonts.
  useCustomFontFaces(quiz?.settings?.customFonts);
  useEffect(() => {
    if (!quiz) return;
    ensureFontsLoaded(quiz.settings?.fontId as string | undefined);
    const themeId = quiz.settings?.themeId as string | undefined;
    const theme = themeId ? getThemeById(themeId) : undefined;
    ensureThemeFontsLoaded(theme?.typography);
    // WEB-672 atom E: per-block font overrides (block.fontId) + the countdown
    // digit font (WEB-696) — load each unique id, otherwise a picked block
    // font renders as the fallback stack. ensureFontsLoaded is idempotent.
    for (const blockFontId of collectBlockFontIds(
      Array.isArray(quiz.content) ? quiz.content : [],
    )) {
      ensureFontsLoaded(blockFontId);
    }
    // WEB-3447, заход 3: ленивая тема («Blue Gradient») приезжает с куском
    // плеера — её шрифт догружаем, когда кусок на руках.
  }, [quiz, playerKitDoor.waiting]);

  // WEB-409: persist the consent decision; on grant load pixels now (deferred at
  // mount) so tracking resumes without a reload.
  //
  // WEB-2145 п.2 / WEB-2147 п.2: решение не сводится к значению куки.
  // `applyConsentDecision` при ОТКАЗЕ стирает уже записанные рекламные метки
  // (`_fbp`, `qb_visitor_id`, …), а при СОГЛАСИИ дозаписывает метки из адресной
  // строки; `revokeTrackingConsent()` говорит уже загруженным пикселям, что
  // согласия больше нет. Без этой пары кнопка «Отклонить» была косметикой:
  // слежка, начатая до отказа, продолжалась.
  const handleConsent = (decision: ConsentDecision) => {
    // WEB-2140: тот же признак проекта, что и у авто-согласия выше, — оба
    // места пишут куку одними правилами домена.
    applyConsentDecision(decision, {
      shareAcrossSubdomains: Boolean(quiz?.consentShareSubdomains),
    });
    // WEB-2604: решение с баннера сразу уходит на сервер отдельным сообщением.
    if (quiz) reportConsentDecision(quiz.projectId, decision);
    setShowConsent(false);
    // WEB-2145 п.3: значок «передумать» показывает действующее решение — он
    // обязан узнать о нём в том же такте, что и скрытие баннера.
    setConsentDecision(decision);
    if (decision === 'denied') {
      revokeTrackingConsent();
    }
    if (decision === 'granted' && quiz) {
      setPlayerPixelIdentity(quiz.funnelIdentity);
      loadPixels(quiz.attribution, 'granted');
      fireMetaBrowserEvent('QUIZ_VIEW');
    }
  };

  // WEB-256: back-button traffback intercept (default-ON).
  // Called unconditionally before early returns (Rules of Hooks).
  // When quiz is null (loading), URLs resolve to null — hook is a no-op.
  // WEB-2141: куда вести человека, вернувшегося «назад» с чужой страницы —
  // решает автор воронки. Значение считается один раз на приехавшие настройки:
  // ловушка держит его в зависимостях эффекта, и новый объект на каждый рендер
  // пересоздавал бы подписку без нужды.
  const returnTarget = useMemo(
    () => readReturnTarget(quiz?.funnelSettings ?? null),
    [quiz?.funnelSettings],
  );

  useTraffback({
    target: { kind: 'quiz', projectId: projectId ?? '', slug: quizSlug ?? '' },
    iosStoreUrl: quiz?.iosStoreUrl ?? null,
    androidStoreUrl: quiz?.androidStoreUrl ?? null,
    returnTarget,
  });

  // WEB-395 (SCR): our apex/subdomains never serve funnels → 404 (no fetch ran).
  if (servingBlocked) {
    return (
      <PublicSystemPageLazy
        kind="page"
        data-testid="apex-not-servable"
        // WEB-2975: воронка не загружалась (наш апекс) — язык только из
        // браузера покупателя, тот же приём, что у «квиз не найден».
        // Язык — браузера покупателя: `strings` не передаётся.
      />
    );
  }

  if (
    isLoading ||
    stringsState === 'wait' ||
    stylePackDoor.waiting ||
    playerKitDoor.waiting
  ) {
    return (
      <div className="public-page-center">
        <Preloader />
      </div>
    );
  }

  if (authRequired) {
    // WEB-1894 п.4: локаль та же, что у соседних экранов, — из браузера
    // посетителя (WEB-916/967/1336).
    return (
      <PublicSystemPageLazy
        kind="author"
        data-testid="public-quiz-auth-required"
      />
    );
  }

  if (unavailable) {
    // WEB-1336 (П2), перенос на квиз (десятка 4, п.1): локаль неоткуда брать,
    // кроме браузера посетителя — тот же приём, что у not-found (WEB-916/967).
    return (
      <PublicSystemPageLazy
        kind="service"
        data-testid="public-quiz-unavailable"
        retryTestId="public-quiz-retry"
        retrying={retrying}
        onRetry={() => {
          setRetrying(true);
          setRetryNonce((n) => n + 1);
        }}
      />
    );
  }

  if (notFound || !quiz) {
    return (
      <PublicSystemPageLazy
        kind="quiz"
        data-testid="public-quiz-not-found"
        // WEB-916 п.4: квиз ещё не загружен (404) — брать локаль неоткуда,
        // кроме браузера посетителя. Тот же приём, что у пейволла (WEB-967).
        // Язык — браузера покупателя: `strings` не передаётся.
      />
    );
  }

  const screens: EditorScreen[] = Array.isArray(quiz.content)
    ? quiz.content
    : [];

  const themeId = quiz.settings?.themeId as string | undefined;
  const resolvedTheme = themeId ? getThemeById(themeId) : undefined;
  // WEB-447 wave1 slice2: the resolved theme's button style drives the player's
  // own next-button + is forwarded to PublicBlockRenderer. Undefined → pill.
  const buttonStyle = resolvedTheme?.buttonStyle;
  const textColorOverride = quiz.settings?.textColor as string | undefined;
  // WEB-942: слои поверхности (база → тема → фон → палитра → цвет текста →
  // стиль-пак → шрифт) собирает ОДНА функция — та же, что красит превью
  // конструктора и встроенный пейволл. Пока стек складывался здесь инлайном,
  // порядок разошёлся с превью и оверрайд цвета текста проигрывал палитре.
  const surfaceStyle = buildFunnelSurfaceStyle(quiz.settings);

  /**
   * ЖАЛОБА ПОКУПАТЕЛЯ 18.09.2026 (воронка funnel.quizsomaapp.info) — ОДИН
   * ИСТОЧНИК ПРАВДЫ ПРО ШАГ ОПЛАТЫ.
   *
   * «Страница открыта ШТАТНЫМ шагом оплаты» = метка `?s=paywall` в адресе И
   * след настоящего перехода в этом браузере (двойная защита WEB-1740,
   * `wantsPaywallStep`) И действующий пейволл, на который перематывать.
   *
   * Это же значение читает `loadQuizProgress`: без него восстановленный флаг
   * «дошёл до пейволла» открывал экран оплаты по чистой рекламной ссылке и
   * запирал квиз на сутки, обходя защиту WEB-1740. Поэтому выражение стоит
   * ОДНОЙ константой и уезжает и в чтение прогресса, и в проп визарда —
   * второго источника правды здесь быть не должно.
   */
  const atPaywallStep =
    wantsPaywallStep &&
    !!resolveFunnelContext(quiz.funnel, previewPaywallSlug, paywallOverrideSlug)
      ?.paywallSlug;

  /**
   * НАХОДКА 1 ТРЕТЬЕГО ПРОХОДА СВЕЖЕГО ВЗГЛЯДА (18.09) — ЧТЕНИЕ ПРОГРЕССА
   * СТАЛО ДЕЙСТВИЕМ, И ЕГО НЕЛЬЗЯ ПОВТОРЯТЬ НА КАЖДЫЙ РЕНДЕР.
   *
   * Чтение без метки шага оплаты СБРАСЫВАЕТ завершённый проход и снимает
   * запись (quizProgressStorage.ts). Поэтому результат берётся один раз на
   * загрузку и держится в ссылке: инициализатор `useState` тут не подходит —
   * на первом рендере квиза ещё нет (идёт загрузка), а `quiz.versionId`
   * появляется только сейчас.
   */
  if (restoredProgressRef.current === undefined) {
    const restored = isPreview
      ? null
      : (loadQuizProgress(quiz.projectId, quiz.id, quiz.versionId, {
          atPaywallStep,
        }) ?? null);
    restoredProgressRef.current = restored;
    /**
     * ОБЕ ПОЛОВИНЫ ОДНОГО ФАКТА УХОДЯТ ВМЕСТЕ (находка 1 прохода 3), И РОВНО
     * ОДИН РАЗ ЗА ЗАГРУЗКУ (находка 1 прохода 4 — это был блокер выката).
     *
     * Чтение сбросило завершённый проход (человек пришёл по чистой рекламной
     * ссылке) — значит права на быструю перемотку у этого браузера больше нет,
     * след WEB-1740 снимается. Иначе возврат на адрес с меткой `?s=paywall`
     * открыл бы экран оплаты БЕЗ ответов, а по ответам считаются правила
     * показа блоков пейволла и выбор тарифа.
     *
     * ПОЧЕМУ ЗДЕСЬ, А НЕ ЭФФЕКТОМ. Эффект жил весь срок страницы, а признак
     * «проход сброшен» оставался истинным до конца загрузки — и стирал след,
     * который человек честно зарабатывал В ТОЙ ЖЕ загрузке, проходя квиз
     * заново: встроенный пейволл объявляет наверх свой стиль, родитель
     * перерисовывается, эффект срабатывает снова. Снятие — часть самого
     * чтения: оно случается там же и тогда же, когда случился сброс, то есть
     * ровно один раз (ссылка выше — защёлка этого «один раз»).
     *
     * Снятие живёт ИМЕННО НА СТРАНИЦЕ, а не в хранилище прогресса: якорь
     * следа — `проект:слаг`, а якорь снимка — `проект:id квиза:версия`; слага
     * хранилище не знает и узнать не может. Страница знает оба.
     *
     * ГРАНИЦА, НАЗВАННАЯ ЧЕСТНО: это правда про ОДНУ вкладку. Если во второй
     * вкладке той же воронки человек стоит на оплате, сброс в этой вкладке
     * снимет и его след (как снимает и общую запись прохождения). Разделяемого
     * между вкладками замка у нас нет; вопрос вынесен координатору.
     */
    if (restored?.restartedAfterCompletion === true) {
      // Якорь тот же, которым страница СПРАШИВАЕТ про след выше
      // (`hasPaywallStepProof(projectId, quizSlug)`): снимать надо ровно тот
      // ключ, который открывает быструю перемотку, а не соседний.
      clearPaywallStepProof(projectId, quizSlug);
    }
  }
  const restoredProgress = restoredProgressRef.current;

  return (
    <main
      data-testid="public-quiz-main"
      className="public-quiz-main"
      style={{
        // WEB-973: min-height живёт в CSS (100dvh с фолбэком) — см. public-paywall.css.
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        // WEB-796: padding переехал в public-paywall.css (база + <=480px
        // override) — инлайн-padding всегда бьёт media-запрос и глушил
        // мобильное ужатие пейволла, который в funnel-пути (WEB-744 §3)
        // рендерится ВНУТРИ этих обёрток, а не через PublicPaywall.tsx.
        // WEB-447 P2-6 / WEB-942: тёплая база, тема, фон, палитра, цвет текста,
        // форма и шрифт — единой сборкой (funnelSurfaceStyle.ts).
        ...surfaceStyle,
        // WEB-921: пока показан встроенный пейволл — страницу красит ЕГО тема
        // (последним слоем, чтобы перебить квизовую); он же снимает объявление
        // при размонтировании, и фон возвращается к квизу.
        ...(funnelPageStyle ?? {}),
      }}
    >
      <div
        className="public-quiz-container"
        style={{
          // WEB-447 §4.3c / DESIGN-LANGUAGE #6 (P0): the white "business card"
          // shell is gone — no surface fill, no radius, no shadow. Content lies
          // directly on the theme/page background so the funnel reads as a
          // designed page, not a form widget. maxWidth 680 stays purely as a
          // readable-line-length constraint; small cards remain only on option/
          // content blocks (rendered per-block), never on the whole column.
          // WEB-796: width/max-width/flex-grow/padding переехали в
          // public-paywall.css (fill-width механизм + <=480px адаптив — тот же
          // приём, что WEB-782 для standalone-пейволла).
          color: 'var(--color-text-primary, #1a1a1a)',
          fontFamily: 'var(--font-family-primary, Inter, sans-serif)',
        }}
        data-testid="public-quiz-container"
        data-quiz-id={quiz.id}
        data-quiz-slug={quiz.slug}
      >
        {screens.length > 0 ? (
          <FunnelPageStyleContext.Provider value={setFunnelPageStyle}>
            <FunnelFaviconContext.Provider value={setEmbeddedFavicon}>
              {/* WEB-1906: адреса магазинов проекта (WEB-256, приходят в
                  публичной выдаче) — кнопке-ссылке с маркером `w2a://store`
                  где угодно в дереве экрана. Пусто = кнопка не рисуется. */}
              <StoreLinksProvider
                ios={quiz.iosStoreUrl}
                android={quiz.androidStoreUrl}
              >
                <FormValuesProvider
                  // WEB-2177 (находка 1 свежего взгляда, проход 3):
                  // FormValuesProvider — родитель QuizScreenWizard в этом
                  // дереве, поэтому восстановленные ответы кладёт СЮДА, а не
                  // сам визард (он ниже провайдера и положить в него
                  // начальное значение снаружи не может). Тот же анкер
                  // (versionId), что и у самого визарда (quizProgressStorage.ts) —
                  // republication со сменой экранов не подставит чужие ответы.
                  // isPreview — по той же причине, что и у восстановления
                  // экрана/флагов в самом визарде: черновик автора не должен
                  // ни читать, ни писать прогресс покупателя.
                  // Жалоба 18.09: та же константа `atPaywallStep`, что и у
                  // пропа визарда ниже — иначе ответы завершённого прохода
                  // подставились бы там, где сам проход уже сброшен.
                  initialValues={restoredProgress?.values}
                  // WEB-2492: экраны квиза — встроенному пейволлу для чтения ответов.
                  screens={screens}
                >
                  {/* WEB-1330 п.2: отдаёт блоку cookie_consent (глубоко внутри
                    QuizScreenWizard) тот же handleConsent, которым пользуется
                    системный ConsentBanner ниже — мгновенная дозагрузка
                    пикселей после решения, без прокидывания коллбэка через
                    пропсы визарда/рендерера. */}
                  <ConsentActionsProvider onConsentDecision={handleConsent}>
                    <QuizScreenWizard
                      screens={screens}
                      logic={(quiz.logic ?? []) as Logic}
                      geoCountry={quiz.geo_country}
                      language={quiz.language}
                      quizSlug={quizSlug!}
                      quizId={quiz.id}
                      projectId={quiz.projectId}
                      // WEB-2177: анкер покупательского прогресса в браузере
                      // (quizProgressStorage.ts) — id последнего снимка
                      // публикации (WEB-848 D3), свежий на каждой
                      // республикации. Undefined только у ответа, закешированного
                      // до появления этого поля — тогда восстановление молча
                      // выключено (нет анкера, нет риска).
                      contentVersion={quiz.versionId}
                      textColor={textColorOverride}
                      showProgress={
                        quiz.settings?.showProgress as boolean | undefined
                      }
                      // Хром экрана (прогресс/счётчик/кнопка назад) из стиль-пака.
                      stylePackId={
                        quiz.settings?.stylePackId as string | undefined
                      }
                      logoUrl={quiz.settings?.logoUrl as string | undefined}
                      // Слово владельца 20.10.2026: шапка квиза своими полями.
                      quizHeader={quiz.settings?.quizHeader}
                      // Слово владельца 21.10.2026: главная кнопка своими полями.
                      quizCta={quiz.settings?.quizCta}
                      // Слово владельца 21.10.2026: вид загрузчика своими полями.
                      quizLoader={quiz.settings?.quizLoader}
                      buttonStyle={buttonStyle}
                      isPreview={isPreview}
                      funnel={resolveFunnelContext(
                        quiz.funnel,
                        previewPaywallSlug,
                        paywallOverrideSlug,
                      )}
                      // Ревизия пачки 29, п.4: метка «ответ из копии на
                      // краю» была объявлена и не читалась — довели до шага
                      // оплаты внутри воронки (там живой запрос к API).
                      servedStale={routeOverride?.servedStale === true}
                      startAtPaywall={atPaywallStep}
                      // Свежий взгляд пачки 46 (находка 2): запрет отдавать
                      // ответы в чужой адрес доезжает до ОБЕИХ веток ухода,
                      // включая кнопку-ссылку (её серверный фильтр не видит,
                      // WEB-2164 п.6).
                      outboundAnswersBlocked={quiz.outboundAnswersBlocked}
                    />
                  </ConsentActionsProvider>
                </FormValuesProvider>
              </StoreLinksProvider>
            </FunnelFaviconContext.Provider>
          </FunnelPageStyleContext.Provider>
        ) : (
          <PublicSystemPageLazy
            kind="funnel"
            strings={getPublicQuizStrings(quiz.language)}
          />
        )}
      </div>
      {showConsent && (
        <ConsentBanner
          onDecision={handleConsent}
          language={quiz.language}
          decision={consentDecision}
          policyUrl={resolveConsentPolicyUrl(quiz.content)}
        />
      )}
      {/* WEB-2145 п.3: тихий значок «передумать». Он поднимает СИСТЕМНЫЙ
          баннер даже там, где первое согласие вёл авторский блок
          cookie_consent: блок после решения прячется сам
          (PublicBlockRendererExtended, CookieConsentBlock), и отозвать
          согласие было бы негде. Адрес политики для поднятого баннера берётся
          из того же авторского блока — первая ступень лестницы. */}
      <PublicFooter
        euVisitor={euVisitor}
        consentDone={consentDecision !== undefined}
        onRecall={() => setShowConsent(true)}
        language={quiz.language}
      />
    </main>
  );
}
